// Global Instructions Rule Applied!
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'
import type { FormInstance } from 'antd'

export interface BusinessRuleResult {
  formData: Record<string, any>
  formUpdates?: Record<string, any>
  helperFieldUpdates?: Record<string, any>
}

/**
 * Handle sourced_by field change - ensure it stays as a flat array
 */
function processSourcedByValue(field: string, value: any): any {
  if (field === DealFields.contact_accounts__sourced_by && Array.isArray(value)) {
    return value.flat()
  }
  return value
}

/**
 * Handle company change - reset funding round helper field
 */
function handleCompanyChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  if (field !== DealFields.deals__account) return null

  return {
    formData: { ...currentFormData, [field]: value },
    helperFieldUpdates: { funding_round: '' }
  }
}

/**
 * Handle new company toggle change
 */
function handleNewCompanyToggle(
  field: string,
  value: boolean,
  currentFormData: Record<string, any>,
  form: FormInstance
): BusinessRuleResult | null {
  if (field !== DealFields.prog__is_new_account) return null

  const newData: Record<string, any> = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}
  const helperFieldUpdates: Record<string, any> = {}

  if (value === true) {
    // When new company = true, clear selected company field
    newData[DealFields.deals__account] = ''
    formUpdates[DealFields.deals__account] = ''

    // Default company name to deal name
    if (currentFormData[DealFields.deals__name]) {
      newData[DealFields.accounts__name] = currentFormData[DealFields.deals__name]
      formUpdates[DealFields.accounts__name] = currentFormData[DealFields.deals__name]
    }
  } else {
    // When new company = false, clear company field and new company fields
    newData[DealFields.deals__account] = ''
    formUpdates[DealFields.deals__account] = ''

    // Clear new company fields
    newData[DealFields.accounts__industry] = ''
    newData[DealFields.accounts__website] = ''
    newData[DealFields.accounts__business_city_code] = ''
    newData[DealFields.accounts__business_country_code] = ''
    newData[DealFields.accounts__business_stateprovince] = ''
    newData[DealFields.accounts__description] = ''

    formUpdates[DealFields.accounts__industry] = ''
    formUpdates[DealFields.accounts__website] = ''
    formUpdates[DealFields.accounts__business_city_code] = ''
    formUpdates[DealFields.accounts__business_country_code] = ''
    formUpdates[DealFields.accounts__business_stateprovince] = ''
    formUpdates[DealFields.accounts__description] = ''

    helperFieldUpdates.company_city_name = ''
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined,
    helperFieldUpdates: Object.keys(helperFieldUpdates).length > 0 ? helperFieldUpdates : undefined
  }
}

/**
 * Handle funding round toggle change
 */
function handleFundingRoundToggle(
  field: string,
  value: boolean,
  currentFormData: Record<string, any>,
  form: FormInstance
): BusinessRuleResult | null {
  if (field !== DealFields.rounds__is_funding_round) return null

  const newData: Record<string, any> = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}

  if (value === false) {
    // When is_funding_round = false, clear all round-related fields
    newData[DealFields.rounds__raise_amount] = ''
    newData[DealFields.rounds__pre_money_valuation] = ''
    newData[DealFields.rounds__post_money_valuation] = ''
    newData[DealFields.rounds__name] = ''
    newData[DealFields.rounds__funding_stage] = ''

    formUpdates[DealFields.rounds__raise_amount] = ''
    formUpdates[DealFields.rounds__pre_money_valuation] = ''
    formUpdates[DealFields.rounds__post_money_valuation] = ''
    formUpdates[DealFields.rounds__name] = ''
    formUpdates[DealFields.rounds__funding_stage] = ''
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
  }
}

/**
 * Handle currency change - warn if funding round deal
 */
function handleCurrencyChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  if (field !== DealFields.deals__currency) return null

  // Business Rule: Currency - If funding round, inherit from Funding Round
  // If set different from Funding Round, alert user
  if (currentFormData[DealFields.rounds__is_funding_round]) {
    console.warn('Currency changed on funding round deal - should match Funding Round currency')
  }

  return {
    formData: { ...currentFormData, [field]: value }
  }
}

/**
 * Handle city change - auto-populate country and state when city is selected
 */
function handleCityChange(field: string, value: any, currentFormData: Record<string, any>): BusinessRuleResult | null {
  if (field !== DealFields.accounts__business_city_code && field !== DealFields.accounts__business_city_label)
    return null

  const newData: Record<string, any> = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}
  const helperFieldUpdates: Record<string, any> = {}

  const countryCode = value?.data?.countryCode || value?.countryCode
  const cityLabel = value?.name || value?.label || ''
  const state = value?.data?.state ?? null

  if (countryCode) {
    newData[DealFields.accounts__business_country_code] = countryCode
    formUpdates[DealFields.accounts__business_country_code] = countryCode

    if (cityLabel) {
      newData[DealFields.accounts__business_city_label] = cityLabel
      formUpdates[DealFields.accounts__business_city_label] = cityLabel
    }

    newData[DealFields.accounts__business_stateprovince] = state || null
    formUpdates[DealFields.accounts__business_stateprovince] = state || null

    helperFieldUpdates.company_city_name = cityLabel
  } else {
    newData[DealFields.accounts__business_stateprovince] = null
    formUpdates[DealFields.accounts__business_stateprovince] = null
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined,
    helperFieldUpdates: Object.keys(helperFieldUpdates).length > 0 ? helperFieldUpdates : undefined
  }
}

/**
 * Handle country change - clear city when country changes
 */
function handleCountryChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>
): BusinessRuleResult | null {
  if (field !== DealFields.accounts__business_country_code && field !== DealFields.accounts__business_country_label)
    return null

  const newData: Record<string, any> = { ...currentFormData, [field]: value }
  const formUpdates: Record<string, any> = {}

  // Extract country code and label from country object
  const countryCode = value?.value || value
  const countryLabel = value?.label || value

  // Clear city and state when country changes
  newData[DealFields.accounts__business_city_code] = null
  newData[DealFields.accounts__business_city_label] = null
  newData[DealFields.accounts__business_stateprovince] = null
  formUpdates[DealFields.accounts__business_city_code] = null
  formUpdates[DealFields.accounts__business_city_label] = null
  formUpdates[DealFields.accounts__business_stateprovince] = null

  // Set country code and label
  if (countryCode) {
    newData[DealFields.accounts__business_country_code] = countryCode
    formUpdates[DealFields.accounts__business_country_code] = countryCode
  }

  if (countryLabel && countryLabel !== countryCode) {
    newData[DealFields.accounts__business_country_label] = countryLabel
    formUpdates[DealFields.accounts__business_country_label] = countryLabel
  }

  return {
    formData: newData,
    formUpdates: Object.keys(formUpdates).length > 0 ? formUpdates : undefined
  }
}

/**
 * Main handler for field changes - applies all business rules
 */
export function handleFieldChange(
  field: string,
  value: any,
  currentFormData: Record<string, any>,
  form: FormInstance
): BusinessRuleResult {
  // Special handling for sourced_by to ensure it stays as a flat array
  let processedValue = value
  if (field === DealFields.contact_accounts__sourced_by && Array.isArray(value)) {
    processedValue = value.flat()
  }

  let newData = { ...currentFormData, [field]: processedValue }

  // Apply business rules in order
  const companyResult = handleCompanyChange(field, processedValue, currentFormData)
  if (companyResult) {
    newData = companyResult.formData
  }

  const newCompanyResult = handleNewCompanyToggle(field, processedValue, newData, form)
  if (newCompanyResult) {
    newData = newCompanyResult.formData
    if (newCompanyResult.formUpdates) {
      form.setFieldsValue(newCompanyResult.formUpdates)
    }
    return newCompanyResult
  }

  const fundingRoundResult = handleFundingRoundToggle(field, processedValue, newData, form)
  if (fundingRoundResult) {
    newData = fundingRoundResult.formData
    if (fundingRoundResult.formUpdates) {
      form.setFieldsValue(fundingRoundResult.formUpdates)
    }
    return fundingRoundResult
  }

  const currencyResult = handleCurrencyChange(field, processedValue, newData)
  if (currencyResult) {
    newData = currencyResult.formData
  }

  const cityResult = handleCityChange(field, processedValue, newData)
  if (cityResult) {
    newData = cityResult.formData
    if (cityResult.formUpdates) {
      form.setFieldsValue(cityResult.formUpdates)
    }
    return cityResult
  }

  const countryResult = handleCountryChange(field, processedValue, newData)
  if (countryResult) {
    newData = countryResult.formData
    if (countryResult.formUpdates) {
      form.setFieldsValue(countryResult.formUpdates)
    }
    return countryResult
  }

  // Default: just update the field
  return {
    formData: newData
  }
}
